import * as React from 'react';
import { View, ScrollView, Switch, Platform, Alert } from 'react-native';
import { Text, Icon, Button } from 'react-native-elements';
import { RNMultiSelect } from 'rn-multipicker';
import * as FileSystem from 'expo-file-system';
import { Formik } from 'formik';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import useMediumScreen from 'hooks/useMediumScreen';
import useChangeAuthData from 'hooks/useChangeAuthData';
import AppContext from 'contexts/AppContext';
import OrderContext from 'contexts/OrderContext';
import { getAllReportingOrders, getReportingOrdersSummary } from 'services/ordersService';
import CommercesScreenProps from 'types/screens/CommercesScreenProps';
import UserRoleType from 'types/enums/UserRoleType';
import ConsumptionReportPayloadData from 'types/models/ConsumptionReportPayloadData';
import CustomSelect from 'components/CustomSelect';
import DateInput from 'components/DateInput';
import CustomCloverPointsSelect from 'components/CustomCloverPointsSelect';
import { styles } from 'styles/screens/consuptionsReportsScreen';
import ConsumptionsReportForm from 'forms/schemas/ConsumptionsReportForm';
import { StorageAccessFramework } from 'expo-file-system';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Buffer } from 'buffer';
import parseAmounts from 'utils/parseAmounts';
import Storage from 'constants/Storage';
import { SupportedLanguages } from 'types/enums/LanguageType';
import { TextInput } from 'react-native-paper';
import ByMethodPaymentResult from 'types/models/ByMethodPaymentResult';
import { Loader } from 'components/Loading';

// Definimos la interfaz del resumen devuelto por el Backend
interface SummaryData {
  total_transactions: number;
  total_amount: number;
}

export default ({ navigation }: CommercesScreenProps): JSX.Element => {
  const { authData, setDisplayMenuModal, setModalData } = React.useContext(AppContext);
  const { currentVenue } = React.useContext(OrderContext);
  const { deleteAuthData } = useChangeAuthData();

  const [total, setTotal] = React.useState<number | null>(null);
  const [page, setPage] = React.useState<number>(1);
  const [summary, setSummary] = React.useState<SummaryData | null>(null);
  const mediumScreen = useMediumScreen();

  const isCommerceEditor = authData?.user.role === UserRoleType.CommerceEditor;
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [paginationOrders, setPaginationOrders] = React.useState<any>(null);
  const [currentFormData, setCurrentFormData] = React.useState<ConsumptionReportPayloadData | null>(null);
  const paymentIdsForSearch = React.useRef<ByMethodPaymentResult[]>([]);

  const { t, i18n } = useTranslation();

  const getReadableFolderName = (directoryUri: string) => {
    try {
      const decodedUri = decodeURIComponent(directoryUri);
      const parts = decodedUri.split(':');
      return parts[parts.length - 1] || t('selected-folder');
    } catch {
      return t('selected-folder');
    }
  };

  const downloadReport = async () => {
    if (!currentFormData) return;

    if (paymentIdsForSearch.current.length > 0) {
      currentFormData.paymentIds = paymentIdsForSearch.current;
    }

    const result = await getAllReportingOrders(currentFormData, i18n.language);
    if (!result) return;

    const fileName = 'reporte_ordenes.xlsx';

    if (Platform.OS === 'web') {
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(
        new Blob([result], {
          type: 'application/octet-stream',
        }),
      );
      a.download = fileName;
      a.click();
    } else if (Platform.OS === 'android' && Platform.Version >= 30) {
      const base64Data = Buffer.from(result).toString('base64');

      let directoryUri = await AsyncStorage.getItem(Storage.reportDirectoryUri);

      if (!directoryUri) {
        await new Promise((resolve) => {
          Alert.alert(t('select-folder'), t('select-or-create-folder'), [{ text: t('accept'), onPress: resolve }]);
        });

        const permissions = await StorageAccessFramework.requestDirectoryPermissionsAsync();
        if (!permissions.granted) {
          Alert.alert(t('permission-denied'), t('cannot-save-file'));
          return;
        }

        directoryUri = permissions.directoryUri;
        await AsyncStorage.setItem(Storage.reportDirectoryUri, directoryUri);
      }

      const fileUri = await StorageAccessFramework.createFileAsync(
        directoryUri,
        fileName,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      );

      await FileSystem.writeAsStringAsync(fileUri, base64Data, {
        encoding: FileSystem.EncodingType.Base64,
      });

      const folderName = getReadableFolderName(directoryUri);

      Alert.alert(t('saved-file'), `${t('file-saved-in-folder')} ${folderName}`);
    }
  };

  const loadData = async () => {
    if (!currentFormData) return;
    setIsLoading(true);
    try {
      const {
        cloverPointIds,
        isCashPayment,
        isCardPayment,
        isQrPayment,
        ...consumptionsReportPayload
      } = currentFormData;
      const paymentsMethodsCollection = [isCashPayment, isCardPayment, isQrPayment];

      if (
        (cloverPointIds?.length === 0 && paymentsMethodsCollection.every((method) => method === false)) ||
        (cloverPointIds?.length === 0 && paymentsMethodsCollection.every((method) => method === true))
      ) {
        const summaryResult = await getReportingOrdersSummary(currentFormData);

        setSummary(summaryResult ?? { total_transactions: 0, total_amount: 0 });
      } else {
        if (consumptionsReportPayload.paymentIds && consumptionsReportPayload.paymentIds.length === 0) {
          setPaginationOrders({
            currentPage: 0,
            data: [],
            perPage: 0,
            total: 0,
          });
          setSummary({ total_transactions: 0, total_amount: 0 });
        } else {
          const summaryResult = await getReportingOrdersSummary(currentFormData);
          setSummary(summaryResult ?? { total_transactions: 0, total_amount: 0 });
        }
      }
    } catch (error) {
      console.log('error loadData', error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (total && paginationOrders && page) {
      setTotal(null);
      setPaginationOrders(null);
      setPage(1);
    }
    if (paymentIdsForSearch.current.length > 0) {
      paymentIdsForSearch.current = [];
    }
    loadData();
  }, [currentFormData]);
  const saveAuthData = async (values: any) => {
    setCurrentFormData({
      ...values,
      dateFrom: moment(moment(values.dateFrom).toDate()).format('yyyy-MM-DD HH:mm:ss'),
      dateTo: moment(moment(values.dateTo).toDate()).format('yyyy-MM-DD HH:mm:ss'),
    });
  };

  const SummaryCards = ({ totalTransactions, totalAmount }: any) => {
    return (
      <View style={styles.containerSummary}>
        {/* Tarjeta 1: Total de Transacciones */}
        <View style={styles.card}>
          <Text style={styles.label}>{t('total-transactions')}</Text>
          <Text style={styles.valueNumber}>{totalTransactions}</Text>
        </View>

        {/* Tarjeta 2: Monto Total */}
        <View style={styles.card}>
          <Text style={styles.label}>{t('total-amount')}</Text>
          <Text style={[styles.valueAmount, {color: currentVenue?.appColor}]}>
            {parseAmounts(
              Number(totalAmount?.toFixed(2)),
              i18n.language as SupportedLanguages,
            )}
          </Text>
        </View>
      </View>
    );
  };

  const TotalContainer = mediumScreen ? View : ScrollView;

  return (
    <TotalContainer style={styles.container}>
      {isLoading && <Loader position="top-center" />}
      <View style={mediumScreen ? styles.header : styles.headerSmall}>
        <Icon
          name="menu-outline"
          type="ionicon"
          color="#000000"
          size={40}
          containerStyle={mediumScreen ? styles.iconLarge : styles.iconSmall}
          onPress={() => setDisplayMenuModal(true)}
        />
        {mediumScreen && (
          <View style={styles.upperTextContainerLarge}>
            <Text style={styles.textTitleLarge}>{`${currentVenue?.name} - ${t('consumptions-reports')}`}</Text>
          </View>
        )}
        {!mediumScreen && (
          <Icon
            name="person-circle-sharp"
            type="ionicon"
            color="#001C47"
            size={40}
            containerStyle={mediumScreen ? styles.iconLarge : styles.iconSmall}
            onPress={() =>
              setModalData({
                description: t('logout'),
                generalColor: currentVenue?.appColor ?? '#001C47',
                action: async () => {
                  await deleteAuthData();
                },
              })
            }
          />
        )}
      </View>

      <View style={mediumScreen ? styles.wrapper : styles.wrapperSmall}>
        <Text style={styles.selectLabel}>{t('commerces')}</Text>
        <Formik
          initialValues={{
            venueId: currentVenue?.id,
            commerceIds: currentVenue?.commerces.map(({ id }) => id) ?? [],
            dateFrom: '',
            dateTo: '',
            identificationNumber: '',
            cloverPointIds: [],
            isCashPayment: false,
            isCardPayment: false,
            isQrPayment: false,
          }}
          onSubmit={(values) => saveAuthData(values)}
          validationSchema={ConsumptionsReportForm()}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({ handleSubmit, setFieldValue, isSubmitting, errors, values }) => (
            <>
              <View style={{ width: '100%' }}>
                <CustomSelect
                  items={currentVenue?.commerces ?? []}
                  callback={(ids: any) => {
                    setFieldValue('commerceIds', ids);
                  }}
                  isCommerceEditor={isCommerceEditor}
                />
                {errors.commerceIds && <Text style={styles.textError}>{errors.commerceIds}</Text>}
              </View>
              <View style={{ width: '100%' }}>
                {currentVenue && currentVenue.paymentConfig?.cloverPointConfigId && (
                  <CustomCloverPointsSelect
                    selectedCommerceIds={values.commerceIds}
                    commerces={currentVenue?.commerces ?? []}
                    venueId={currentVenue.id}
                    cloverConfigIdFromVenue={currentVenue.paymentConfig.cloverPointConfigId}
                    callback={(value) => {
                      setFieldValue('cloverPointIds', value);
                    }}
                    venueColor={currentVenue.appColor ?? '#000000'}
                    RNMultiSelect={RNMultiSelect}
                  />
                )}
              </View>
              <View style={{ width: '100%' }}>
                <Text style={styles.selectLabel}>{t('identification-number')}</Text>
                <TextInput
                  keyboardType="number-pad"
                  style={[styles.textInput]}
                  onChangeText={(value) => setFieldValue('identificationNumber', value)}
                  placeholder={t('identification-number')}
                  value={values.identificationNumber}
                  placeholderTextColor={'#959393'}
                />
              </View>
              <View style={mediumScreen ? styles.switchesContainer : styles.switchesContainerSmall}>
                <View style={styles.switchIsCashPaymentContainer}>
                  <Text style={styles.switchTitle}>{t('cash-order')}</Text>
                  <Switch
                    trackColor={{ false: '#001639', true: currentVenue?.appColor }}
                    thumbColor={'#001639'}
                    onValueChange={(value) => {
                      setFieldValue('isCashPayment', value);
                    }}
                    {...Platform.select({
                      web: {
                        activeThumbColor: currentVenue?.appColor,
                      },
                    })}
                    value={values.isCashPayment}
                    style={styles.switch}
                    disabled={isSubmitting}
                  />
                </View>
                <View style={styles.switchIsCardPaymentContainer}>
                  <Text style={styles.switchTitle}>{t('card-order')}</Text>
                  <Switch
                    trackColor={{ false: '#001639', true: currentVenue?.appColor }}
                    thumbColor={'#001639'}
                    onValueChange={(value) => {
                      setFieldValue('isCardPayment', value);
                    }}
                    {...Platform.select({
                      web: {
                        activeThumbColor: currentVenue?.appColor,
                      },
                    })}
                    value={values.isCardPayment}
                    style={styles.switch}
                    disabled={isSubmitting}
                  />
                </View>
                <View style={styles.switchIsFiservQrPaymentContainer}>
                  <Text style={styles.switchTitle}>{t('fiserv-qr-order')}</Text>
                  <Switch
                    trackColor={{ false: '#001639', true: currentVenue?.appColor }}
                    thumbColor={'#001639'}
                    onValueChange={(value) => {
                      setFieldValue('isQrPayment', value);
                    }}
                    {...Platform.select({
                      web: {
                        activeThumbColor: currentVenue?.appColor,
                      },
                    })}
                    value={values.isQrPayment}
                    style={styles.switch}
                    disabled={isSubmitting}
                  />
                </View>
              </View>
              <View style={[styles.dateInputContainer, !mediumScreen && { alignItems: 'center' }]}>
                <DateInput
                  isMediumScreen={mediumScreen}
                  text={t('from')}
                  onChange={(date: any) => {
                    const formatDate = moment(date, 'YYYY-MM-DD HH:mm')
                      .tz(currentVenue?.localDateTimeIdentifier ?? '')
                      .toDate();
                    setFieldValue('dateFrom', formatDate);
                  }}
                />
                <DateInput
                  isMediumScreen={mediumScreen}
                  text={t('to')}
                  onChange={(date: any) => {
                    const formatDate = moment(date, 'YYYY-MM-DD HH:mm')
                      .tz(currentVenue?.localDateTimeIdentifier ?? '')
                      .toDate();
                    setFieldValue('dateTo', formatDate);
                  }}
                />
              </View>
              <View style={styles.datesErrorContainer}>
                {errors.dateFrom && <Text style={styles.textError}>{errors.dateFrom}</Text>}
                {errors.dateTo && <Text style={styles.textError}>{errors.dateTo}</Text>}
              </View>
              <View
                style={
                  mediumScreen ? styles.registerOrderButtonContainerLarge : styles.registerOrderButtonContainerSmall
                }
              >
                <Button
                  title={t('search')}
                  type="solid"
                  containerStyle={mediumScreen ? styles.orderBtnContainerLarge : styles.orderBtnContainerSmall}
                  titleStyle={styles.btnTitleOrders}
                  buttonStyle={mediumScreen ? styles.orderBtnLarge : styles.orderBtnSmall}
                  onPress={() => {
                    handleSubmit();
                  }}
                  disabled={isSubmitting}
                />
              </View>
            </>
          )}
        </Formik>
      </View>

      {summary ? (
        <>
          <SummaryCards totalTransactions={summary.total_transactions} totalAmount={summary.total_amount} />
          {(Platform.OS === 'web' || (Platform.OS === 'android' && Platform.Version >= 30)) && (
            <View style={styles.reportDownloadContainer}>
              <Button title={t('download-report')} type="outline" onPress={downloadReport} />
            </View>
          )}
        </>
      ) : null}
    </TotalContainer>
  );
};
